/**
 * Builder section 4 — the audiences. Each gets its own share of every budget
 * option, its own targeting (and targeting cost) and its own added value, the
 * way the State Farm plan splits General Market / Women / Hispanic.
 */

import React from 'react';
import { Button } from '../../../ui';
import type { MediaPlanUnitDef } from '../types';
import { remainingSegmentPct, segmentShares, type PlanDraft, type SegmentDraft } from '../planDraft';
import { MAX_SEGMENTS } from '../vocab';
import FormSection from './FormSection';
import AudienceCard from './AudienceCard';
import { FieldError, type ErrorFor } from './fields';

interface Props {
  draft: PlanDraft;
  units: MediaPlanUnitDef[];
  onSegmentChange: (id: string, patch: Partial<SegmentDraft>) => void;
  onAddSegment: () => void;
  onRemoveSegment: (id: string) => void;
  errorFor: ErrorFor;
  disabled?: boolean;
}

function shareStatus(segments: readonly SegmentDraft[]): { text: string; ok: boolean } | null {
  if (segments.length < 2) return null;
  if (segments.every((s) => !s.budgetPct.trim())) return { text: 'Equal split', ok: true };
  const remaining = remainingSegmentPct(segments);
  if (Math.abs(remaining) < 0.01 && segmentShares(segments)) return { text: 'Shares total 100%', ok: true };
  if (remaining > 0) return { text: `${remaining}% unassigned`, ok: false };
  return { text: `${Math.abs(remaining)}% over`, ok: false };
}

const AudiencesSection: React.FC<Props> = ({ draft, units, onSegmentChange, onAddSegment, onRemoveSegment, errorFor, disabled = false }) => {
  const shares = segmentShares(draft.segments);
  const status = shareStatus(draft.segments);
  return (
    <FormSection
      id="mp-section-audiences"
      title="Audiences"
      note="Each audience gets its own share of every budget, its own targeting and its own added value."
      action={
        draft.segments.length < MAX_SEGMENTS ? (
          <Button size="sm" variant="subtle" disabled={disabled} onClick={onAddSegment}>
            + Add audience
          </Button>
        ) : undefined
      }
    >
      {status && (
        <div style={{ fontSize: 12, fontWeight: 700, color: status.ok ? 'var(--success)' : 'var(--warning)' }}>{status.text}</div>
      )}
      <FieldError field="segments" message={errorFor('segments')} />
      {draft.segments.map((s, i) => (
        <AudienceCard
          key={s.id}
          segment={s}
          index={i}
          count={draft.segments.length}
          budgetUsd={draft.budgetUsd}
          sharePct={shares ? shares[i] : null}
          units={units}
          onChange={(patch) => onSegmentChange(s.id, patch)}
          onRemove={() => onRemoveSegment(s.id)}
          errorFor={errorFor}
          disabled={disabled}
        />
      ))}
    </FormSection>
  );
};

export default AudiencesSection;
